import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@futuredev/shared-auth/api';
import { useAuth } from '@futuredev/shared-auth';
import { extractApiError, getApiStatus } from '@futuredev/shared-system/formatters';

export interface CartItem {
  id: number;
  productId?: number;
  quantity: number;
  [key: string]: unknown;
}

export interface Order {
  id: number;
  items: CartItem[];
  [key: string]: unknown;
}

export interface CartContextValue {
  cartItems: CartItem[];
  cartSize: number;
  order: Order | null;
  loading: boolean;
  error: string | null;
  addToCart: (productId: number, quantity?: number, shippingOption?: string) => Promise<{ success: boolean; size: number }>;
  addMembershipPlanToCart: (planCode: string) => Promise<{ success: boolean }>;
  removeFromCart: (itemId: number, cartId: number) => Promise<void>;
  updateCartItemQuantity: (items: unknown[]) => Promise<void>;
  loadCart: () => Promise<void>;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartSize, setCartSize] = useState<number>(0);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadCart = useCallback(async () => {
    try {
      console.log('🛒 loadCart called - user:', user?.id, 'role:', user?.role || user?.user_role || user?.userRole);
      setLoading(true);
      setError(null);

      if (user) {
        const userRole = user.role || user.user_role || user.userRole;
        const isRetailUser = userRole === 'MARKETPLACE_RETAIL_USER';
        console.log('🛒 loadCart - isRetailUser:', isRetailUser);

        if (isRetailUser) {
          const response = await api.get('/order-marketplace/current');
          const order = response.data?.data;
          console.log('🛒 Load cart - API response:', response.data);
          console.log('🛒 Load cart - Order:', order);
          console.log('🛒 Load cart - Order items:', order?.items);

          if (order) {
            const items: CartItem[] = Array.isArray(order.items) ? order.items : [];
            console.log('🛒 Load cart - Processed items:', items);
            setOrder(order);
            setCartItems(items);
            const total = items.reduce((sum: number, item: CartItem) => sum + (item.quantity || 0), 0);
            setCartSize(total);
            console.log('🛒 Load cart - Final state - items:', items.length, 'total:', total);
          } else {
            console.log('🛒 Load cart - No order found');
            setOrder(null);
            setCartItems([]);
            setCartSize(0);
          }
        } else {
          setOrder(null);
          setCartItems([]);
          setCartSize(0);
        }
      } else {
        const guestId = localStorage.getItem('guestUserId');
        if (guestId) {
          const response = await api.get(`/order-marketplace/guest/${guestId}`);
          const order = response.data?.data;

          if (order) {
            const items: CartItem[] = Array.isArray(order.items) ? order.items : [];
            setOrder(order);
            setCartItems(items);
            const total = items.reduce((sum: number, item: CartItem) => sum + (item.quantity || 0), 0);
            setCartSize(total);
          } else {
            setOrder(null);
            setCartItems([]);
            setCartSize(0);
          }
        } else {
          setOrder(null);
          setCartItems([]);
          setCartSize(0);
        }
      }
    } catch (err: unknown) {
      const status = getApiStatus(err);
      if (status === 404 || status === 401 || status === 403) {
        setOrder(null);
        setCartItems([]);
        setCartSize(0);
        setError(null);
      } else {
        console.error('Error loading cart:', err);
        setError(extractApiError(err, 'Cart is temporarily unavailable. Please try again later.'));
        setOrder(null);
        setCartItems([]);
        setCartSize(0);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadCart();
  }, [user?.id, loadCart]);

  const addToCart = async (productId: number, quantity: number = 1, shippingOption: string = 'STANDARD'): Promise<{ success: boolean; size: number }> => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.post('/order-marketplace/add', {
        productId,
        quantity,
        shippingOption,
        guestUserId: user ? undefined : localStorage.getItem('guestUserId')
      });

      if (response.data?.success) {
        if (response.data.data?.guestId) {
          localStorage.setItem('guestUserId', response.data.data.guestId);
        }

        await loadCart();

        return { success: true, size: response.data.data?.size || cartSize + quantity };
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (err: unknown) {
      const errorMessage = extractApiError(err, 'Failed to add item to cart');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId: number, cartId: number): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await api.delete(`/order-marketplace/item/${itemId}/${cartId}`);
      await loadCart();
    } catch (err: unknown) {
      const errorMessage = extractApiError(err, 'Failed to remove item from cart');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateCartItemQuantity = async (items: unknown[]): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      let cartId: number | undefined;
      if (user) {
        const userRole = user.role || user.user_role || user.userRole;
        const isRetailUser = userRole === 'MARKETPLACE_RETAIL_USER';

        if (isRetailUser) {
          const response = await api.get('/order-marketplace/current');
          cartId = response.data?.data?.id;
        }
      } else {
        const guestId = localStorage.getItem('guestUserId');
        if (guestId) {
          const response = await api.get(`/order-marketplace/guest/${guestId}`);
          cartId = response.data?.data?.id;
        }
      }

      if (!cartId) {
        throw new Error('Cart not found');
      }

      await api.post(`/order-marketplace/items/${cartId}`, items);
      await loadCart();
    } catch (err: unknown) {
      const errorMessage = extractApiError(err, 'Failed to update cart');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const clearCart = (): void => {
    setCartItems([]);
    setCartSize(0);
    setOrder(null);
  };

  const addMembershipPlanToCart = async (planCode: string): Promise<{ success: boolean }> => {
    try {
      setLoading(true);
      setError(null);
      console.log('🛒 Adding membership plan to cart:', planCode);
      const response = await api.post('/membership/cart', { planCode });
      console.log('🛒 API Response:', response.data);

      if (response.data?.success && response.data?.data) {
        const order = response.data.data;
        console.log('🛒 Order from response:', order);
        console.log('🛒 Order items:', order.items);

        if (order) {
          const items: CartItem[] = Array.isArray(order.items) ? order.items : [];
          console.log('🛒 Processed items array:', items);
          setOrder(order);
          setCartItems(items);
          const total = items.reduce((sum: number, item: CartItem) => sum + (item.quantity || 0), 0);
          setCartSize(total);
          console.log('🛒 Cart updated - items:', items.length, 'total:', total);
        }
      }

      console.log('🛒 Reloading cart...');
      await loadCart();
      console.log('🛒 Cart reloaded');
      return { success: true };
    } catch (err: unknown) {
      console.error('🛒 Error adding membership to cart:', err);
      const errorMessage = extractApiError(err, 'Failed to add membership plan to cart');
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const value: CartContextValue = {
    cartItems,
    cartSize,
    order,
    loading,
    error,
    addToCart,
    addMembershipPlanToCart,
    removeFromCart,
    updateCartItemQuantity,
    loadCart,
    clearCart
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
